/**
 * MagicLinkPage — invisible interstitial that consumes ?token=... from
 * a comp-audit magic-link email, exchanges it for a real JWT, stores
 * it in localStorage under the key the AuthProvider watches, and then
 * hard-redirects into the target audit report.
 *
 * Hard-redirect (not react-router `Navigate`) is intentional: the
 * AuthProvider only reads `le_token` on mount, so we need a fresh
 * page load for the new session to be picked up on `/analysis/:id`.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';

export default function MagicLinkPage() {
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = (params.get('token') || '').trim();
    if (!token) {
      setError('Missing magic link token. Please use the button from your email.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/auth/magic/exchange', { token });
        if (cancelled) return;
        localStorage.setItem('le_token', data.token);
        // Hard-redirect so AuthProvider re-reads le_token on mount.
        const dest = data.redirect || `/analysis/${data.analysis_id}`;
        window.location.replace(dest);
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || err?.message || 'Something went wrong.';
        setError(detail);
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50" data-testid="magic-link-error">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.72-3l-7.07-12a2 2 0 00-3.44 0L3.2 16a2 2 0 001.73 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Link couldn&apos;t be opened</h1>
          <p className="text-sm text-gray-600 mb-6">{error}</p>
          <a
            href="/contact"
            className="inline-block bg-gray-900 text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-gray-800 transition"
            data-testid="magic-link-error-contact"
          >
            Get a fresh link
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50" data-testid="magic-link-loading">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm">
        <div className="w-10 h-10 mx-auto mb-5 rounded-full border-2 border-gray-900 border-t-transparent animate-spin" />
        <h1 className="text-lg font-semibold text-gray-900 mb-1">Signing you in…</h1>
        <p className="text-sm text-gray-500">One tap and you&apos;re inside — hang tight.</p>
      </div>
    </div>
  );
}
